import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getBlogById, getBlogs } from "../api/services";
import { Calendar, Clock, User, Linkedin, Facebook, Twitter, Share2, ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import CtaBanner from "../components/CtaBanner";
import HtmlContent from "../components/HtmlContent";
import PageBanner from "../components/PageBanner";
import { type, typeBodyHtml } from "../lib/typography";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogDetailPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getBlogById(id), getBlogs()])
      .then(([blog, blogs]) => {
        if (cancelled) return;
        setPost(blog);
        setRelated(blogs.filter((p) => p.id !== String(id)).slice(0, 3));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500">Loading article...</div>
      </section>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <PageBanner
        title={post.title}
        image={post.image}
        breadcrumbTrail={[
          { to: "/blog", label: "Blog" },
          { label: "Article" },
        ]}
      />

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 grid lg:grid-cols-[1fr_220px] gap-12">
          <article className="prose max-w-none">
            <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-[#1e6fcf] text-white">
              {post.category}
            </span>
            <div className={`mt-5 flex flex-wrap items-center gap-5 ${type.meta} text-slate-600`}>
              <span className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#1e6fcf]" /> {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#1e6fcf]" /> {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#1e6fcf]" /> {post.readTime}
              </span>
            </div>
            <div className="mt-8 space-y-6">
              {post.content.map((block, i) => {
                if (block.type === "html") {
                  return (
                    <HtmlContent
                      key={i}
                      html={block.html}
                      className={`${typeBodyHtml} text-slate-700`}
                    />
                  );
                }
                if (block.type === "h2") {
                  return (
                    <h2 key={i} className={`${type.sectionTitle} text-[#0a1a2f] pt-4`}>
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "ul") {
                  return (
                    <ul key={i} className="space-y-2.5">
                      {block.items.map((it) => (
                        <li key={it} className={`flex items-start gap-3 ${type.body} text-slate-700`}>
                          <span className="mt-2 w-1.5 h-1.5 bg-[#1e6fcf] rounded-full flex-shrink-0" />
                          <span className="leading-relaxed">{it}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === "p") {
                  return (
                    <HtmlContent key={i} html={block.text} className={`${typeBodyHtml} text-slate-700`} />
                  );
                }
                return (
                  <HtmlContent key={i} html={block.text} className={`${typeBodyHtml} text-slate-700`} />
                );
              })}
            </div>

            {post.tags?.length > 0 && (
              <div className="mt-12 pt-8 border-t border-slate-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-bold mr-2">Tags:</span>
                  {post.tags.map((t) => (
                    <span key={t} className="text-xs font-semibold px-3 py-1.5 bg-[#e8f2fc] text-[#1758a8] rounded-md">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <Link
              to="/blog"
              className="mt-10 inline-flex items-center gap-2 text-[#1e6fcf] hover:text-[#1758a8] font-bold text-sm uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-32">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-4 flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Share Article
              </div>
              <div className="flex flex-col gap-3">
                <a href="#" className="w-11 h-11 rounded-md bg-slate-100 hover:bg-[#1e6fcf] hover:text-white text-slate-600 flex items-center justify-center transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="#" className="w-11 h-11 rounded-md bg-slate-100 hover:bg-[#1e6fcf] hover:text-white text-slate-600 flex items-center justify-center transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
                <a href="#" className="w-11 h-11 rounded-md bg-slate-100 hover:bg-[#1e6fcf] hover:text-white text-slate-600 flex items-center justify-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-0.5 bg-[#1e6fcf]" />
                  <span className={`${type.eyebrow} text-[#1758a8]`}>Keep Reading</span>
                </div>
                <h2 className={`mt-4 ${type.sectionTitle} text-[#0a1a2f]`}>
                  Related Articles
                </h2>
              </div>
              <Link to="/blog" className="hidden lg:inline-flex items-center gap-2 text-[#0a1a2f] hover:text-[#1e6fcf] font-bold text-sm uppercase tracking-wider transition-colors">
                View All <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.id}`}
                  className="group bg-white rounded-xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-xl transition-all duration-500 overflow-hidden"
                >
                  <div className="h-44 overflow-hidden">
                    {p.image ? (
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0a1a2f] via-[#0f2540] to-[#1e6fcf]/30 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-white/25" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#1758a8]">{p.category}</div>
                    <h3 className={`mt-2 ${type.cardTitle} text-[#0a1a2f] group-hover:text-[#1e6fcf] transition-colors`}>
                      {p.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-1.5 text-[#1e6fcf] font-bold text-xs">
                      Read article
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBanner />
    </>
  );
}
